import type { ApiError } from '@approvalmax/types';
import { errorHelpers } from '@approvalmax/utils';
import { useQuery } from '@tanstack/react-query';
import { domain, schemas, stateTree } from 'modules/data';
import { hooks as pageHooks } from 'modules/page';
import { normalize } from 'normalizr';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { api } from 'services/api';

import { EQueryKeys } from '../../config';
import { loadPageData } from '../actions';

const loadCompany = async (companyId: string): Promise<Partial<stateTree.Entities>> => {
    const result = await api.companies.select({ companyId });
    const { entities } = normalize(result, { Companies: [schemas.companySchemaLegacy] });

    if (!entities.companies || !entities.companies[companyId]) {
        throw errorHelpers.notFoundError();
    }

    return entities;
};

export const usePageData = () => {
    const dispatch = useDispatch();
    const { companyId } = useParams<{ companyId: string }>();
    const { company } = pageHooks.useNonExistentCompanyError(companyId);
    const isAuditor = company?.myRole === domain.CompanyUserRole.Auditor;
    const isManager = company?.myRole === domain.CompanyUserRole.Manager;
    const isWorkflowManager = company?.myRole === domain.CompanyUserRole.WorkflowManager;

    // TODO: Rewrite this to the shared/data
    const { data, isInitialLoading, error, isSuccess } = useQuery<Partial<stateTree.Entities>, ApiError>(
        [EQueryKeys.Users, companyId],
        async () => {
            return await loadCompany(companyId);
        },
        {
            enabled: isManager || isWorkflowManager,
        }
    );

    useEffect(() => {
        if (isSuccess) {
            dispatch(loadPageData(companyId, data));
        }
    }, [companyId, data, dispatch, isSuccess]);

    useEffect(() => {
        if (isAuditor) {
            dispatch(loadPageData(companyId, {}));
        }
    }, [companyId, dispatch, isAuditor]);

    pageHooks.usePageError(error);

    return { isInitialLoading };
};
